import { FC, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import type { ChartEvent } from "@ortex-charts/financial";
import { getChartDataNew } from "@/controllers/chart-data";
import { Stock } from "@/models/stock";
import { MixedEventStat } from "@/models/event-page";
import TimeSeriesChart, { type RangeButton, type TimeSeries } from "@/components/ortex-charts/TimeSeriesChart";
import styles from "./EventPageRevamp.module.scss";

interface PriceEventsChartProps {
    stock: Stock;
    /** Mixed (non-earnings + earnings) per-event reactions, plotted as markers. */
    mixedStats: MixedEventStat[] | null | undefined;
}

const initials = (name: string) =>
    (name ?? "")
        .split(" ")
        .map((w) => w[0]?.toUpperCase() || "")
        .join("");

const fmt = (v: number | null | undefined) => (v == null ? "N/A" : v.toFixed(2));

/** Event markers from the mixed-event statistics, one badge per event with the reaction figures in the tooltip. */
export const buildEventMarkers = (mixedStats: MixedEventStat[] | null | undefined): ChartEvent[] =>
    (mixedStats ?? [])
        .filter((s) => s && s.date != null)
        .sort((a, b) => (a.date ?? 0) - (b.date ?? 0))
        .map((event, i) => ({
            id: `evt-${i}`,
            time: event.date as number,
            style: "badge",
            text: initials(event.name ?? "") || "•",
            group: event.name ?? "",
            tooltip: [event.name ?? "Event", `Week before: ${fmt(event["1WeekAgo"])}%`, `Next trading day: ${fmt(event["1DayAfter"])}%`, `Week after: ${fmt(event["1WeekAfter"])}%`, `Month after: ${fmt(event["1MonthAfter"])}%`],
        }));

const EVENT_RANGES: RangeButton[] = [
    { label: "1m", days: 31 },
    { label: "YTD", days: Math.max(1, dayjs().diff(dayjs().startOf("year"), "day")) },
    { label: "1y", days: 366 },
    { label: "3y", days: 3 * 366 },
    { label: "5y", days: 5 * 366 },
    { label: "All", days: 0 },
];

/** Price line with event markers, one year in view by default. */
const PriceEventsChart: FC<PriceEventsChartProps> = ({ stock, mixedStats }) => {
    const [price, setPrice] = useState<[number, number][]>([]);

    useEffect(() => {
        if (!stock.tradingitem_id) return;
        const today = dayjs();
        getChartDataNew(stock.tradingitem_id, "c", today.subtract(5, "years").format("YYYY-MM-DD"), today.format("YYYY-MM-DD"))
            .then((rows) => setPrice(Array.isArray(rows) ? rows.map((row) => [row[0], row[1]] as [number, number]) : []))
            .catch(() => setPrice([]));
    }, [stock.tradingitem_id]);

    const series = useMemo<TimeSeries[]>(() => [{ id: "price", title: `${stock.ticker} Price in ${stock.currency}`, type: "line", data: price, color: "#31ABA6", precision: 2, lastValue: true }], [price, stock.ticker, stock.currency]);
    const events = useMemo(() => buildEventMarkers(mixedStats), [mixedStats]);

    return (
        <div className={styles.chartContainer}>
            <TimeSeriesChart series={series} events={events} eventLegend rangeButtons={EVENT_RANGES} initialRange="1y" exportName={`${stock.ticker}-events`} />
        </div>
    );
};

export default PriceEventsChart;
